import type { SerializedError } from '@reduxjs/toolkit';
import type { BaseQueryFn } from '@reduxjs/toolkit/dist/query';
import type { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { AxiosRequestConfig } from 'axios';
import axios from 'axios';
import { REHYDRATE } from 'redux-persist';

import { getRequestOptions } from '@shared/utils/api/configs/requestOption';
import { parseResponse } from '@shared/utils/api/handlers/parseResponse/parseResponse';

export interface BaseQueryFnArgument {
  data?: AxiosRequestConfig['data'];
  isErrMsgConcatErrCode?: boolean;
  method: AxiosRequestConfig['method'];
  overrideOptions?: AxiosRequestConfig;
  params?: AxiosRequestConfig['params'];
  url: string;
}

export type BuilderType<T extends string = string> = EndpointBuilder<
  BaseQueryFn<BaseQueryFnArgument, unknown, SerializedError, unknown>,
  T,
  string
>;

interface AxiosBaseQuery {
  isIncludedClientMessageId?: boolean;
}

const axiosBaseQueryDemo =
  ({
    isIncludedClientMessageId,
  }: AxiosBaseQuery): BaseQueryFn<BaseQueryFnArgument, unknown, SerializedError, unknown> =>
  async ({ data, isErrMsgConcatErrCode, method, overrideOptions, params, url }) => {
    try {
      const response = await axios({
        data,
        method,
        params,
        url,
        ...getRequestOptions(true, isIncludedClientMessageId, data?.CustomSourceApp),
        ...overrideOptions,
      });

      return { data: parseResponse(response, isErrMsgConcatErrCode) };
    } catch (error) {
      return {
        error: {
          code: (error as SerializedError).code ?? '',
          message: (error as SerializedError).message ?? '',
        },
      };
    }
  };

export const baseApi = createApi({
  baseQuery: axiosBaseQueryDemo({
    isIncludedClientMessageId: true,
  }),
  endpoints: () => ({}),
  extractRehydrationInfo: (action, { reducerPath }) => {
    if (action.type === REHYDRATE) {
      return action.payload?.[reducerPath];
    }
    return undefined;
  },
  reducerPath: 'baseApi',
  refetchOnMountOrArgChange: 600,
});
